import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

type Line = {
  id: number;
  code: string;
  name: string;
  couponTranche: string | null;
  isActive: boolean;
  supplierId: number | null;
  supplierName: string | null;
};

type Supplier = { id: number; name: string };

export default function Lines() {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [supplierName, setSupplierName] = useState('');
  // Portion « Fournisseurs » repliable.
  const [fournisseursOuverts, setFournisseursOuverts] = useState(true);

  const list = useQuery<{ lines: Line[] }>({
    queryKey: ['admin', 'lines'],
    queryFn: () => api('/api/admin/lines'),
  });
  // Fournisseurs = les « groupes » de la section Cadeaux (notion unique). On ne
  // lit ici que id + nom pour la gestion et le rattachement des lignes.
  const suppliers = useQuery<{ groups: Supplier[] }>({
    queryKey: ['admin', 'rewards'],
    queryFn: () => api('/api/admin/rewards'),
  });
  const supplierList = suppliers.data?.groups ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'lines'] });
  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'lines'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'rewards'] });
  };

  const create = useMutation({
    mutationFn: (body: { code: string; name: string }) =>
      api('/api/admin/lines', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      setCode('');
      void invalidate();
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      api(`/api/admin/lines/${id}`, { method: 'PATCH', body: JSON.stringify({ isActive }) }),
    onSuccess: invalidate,
  });

  // Tranche de coupon : propagée à toutes les lignes du même fournisseur (endpoint
  // dédié). Une ligne sans fournisseur ne touche qu'elle-même.
  const setTranche = useMutation({
    mutationFn: ({ id, couponTranche }: { id: number; couponTranche: number | null }) =>
      api(`/api/admin/lines/${id}/coupon-tranche`, {
        method: 'PATCH',
        body: JSON.stringify({ couponTranche }),
      }),
    onSuccess: invalidate,
  });

  // Rattacher une ligne à un fournisseur (ou l'en détacher).
  const assignSupplier = useMutation({
    mutationFn: ({ id, groupId }: { id: number; groupId: number | null }) =>
      api(`/api/admin/lines/${id}/supplier`, { method: 'PATCH', body: JSON.stringify({ groupId }) }),
    onSuccess: invalidate,
  });

  // Gestion des fournisseurs (réutilise les routes des « groupes » de cadeaux).
  const createSupplier = useMutation({
    mutationFn: (nom: string) =>
      api('/api/admin/rewards/groups', { method: 'POST', body: JSON.stringify({ name: nom, lineIds: [] }) }),
    onSuccess: () => {
      setSupplierName('');
      invalidateAll();
    },
  });
  const renameSupplier = useMutation({
    mutationFn: ({ id, nom }: { id: number; nom: string }) =>
      api(`/api/admin/rewards/groups/${id}`, { method: 'PATCH', body: JSON.stringify({ name: nom }) }),
    onSuccess: invalidateAll,
  });
  const deleteSupplier = useMutation({
    mutationFn: (id: number) => api(`/api/admin/rewards/groups/${id}`, { method: 'DELETE' }),
    onSuccess: invalidateAll,
  });

  return (
    <div className="space-y-6">
      <section className="bg-card rounded-xl border p-6">
        <h2 className="mb-4 text-lg font-semibold">Nouvelle ligne</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // La ligne n'a plus de nom propre : son nom = son code (le nom lisible
            // est porté par le fournisseur auquel elle est rattachée).
            if (code.trim()) create.mutate({ code: code.trim(), name: code.trim() });
          }}
          className="flex flex-wrap items-end gap-3"
        >
          <label className="space-y-1.5">
            <span className="block text-sm font-medium">Code de ligne</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="VAL"
              maxLength={16}
              className="border-input bg-background h-11 w-40 rounded-lg border px-3 font-mono"
            />
          </label>
          <button
            type="submit"
            disabled={create.isPending}
            className="bg-primary text-primary-foreground h-11 rounded-lg px-5 font-medium disabled:opacity-50"
          >
            Ajouter
          </button>
        </form>
        {create.isError && (
          <p className="text-destructive mt-3 text-sm font-medium">{(create.error as Error).message}</p>
        )}
      </section>

      <section className="bg-card rounded-xl border p-6">
        <button
          type="button"
          onClick={() => setFournisseursOuverts((v) => !v)}
          aria-expanded={fournisseursOuverts}
          className="flex w-full items-center gap-2 text-left"
        >
          <span aria-hidden className="text-muted-foreground text-xl">
            {fournisseursOuverts ? '▾' : '▸'}
          </span>
          <h2 className="text-lg font-semibold">Fournisseurs ({supplierList.length})</h2>
        </button>

        {fournisseursOuverts && (
          <>
            <p className="text-muted-foreground mt-2 mb-4 text-sm">
              Un fournisseur regroupe une ou plusieurs lignes de code. Ce regroupement servira au
              calcul du total des cadeaux et du nombre de coupons de tirage.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (supplierName.trim()) createSupplier.mutate(supplierName.trim());
              }}
              className="mb-4 flex flex-wrap items-end gap-3"
            >
          <label className="space-y-1.5">
            <span className="block text-sm font-medium">Nouveau fournisseur</span>
            <input
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              placeholder="Ex. Valvoline"
              className="border-input bg-background h-11 w-64 rounded-lg border px-3"
            />
          </label>
          <button
            type="submit"
            disabled={createSupplier.isPending || !supplierName.trim()}
            className="bg-primary text-primary-foreground h-11 rounded-lg px-5 font-medium disabled:opacity-50"
          >
            Ajouter le fournisseur
          </button>
        </form>

        {supplierList.length === 0 ? (
          <p className="text-muted-foreground text-sm">Aucun fournisseur pour l'instant.</p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {supplierList.map((s) => (
              <SupplierRow
                key={s.id}
                supplier={s}
                lignes={(list.data?.lines ?? []).filter((l) => l.supplierId === s.id)}
                onRename={(nom) => renameSupplier.mutate({ id: s.id, nom })}
                onDelete={() => deleteSupplier.mutate(s.id)}
              />
            ))}
          </ul>
        )}
            {(createSupplier.isError || renameSupplier.isError || deleteSupplier.isError) && (
              <p className="text-destructive mt-3 text-sm font-medium">
                {
                  ((createSupplier.error ?? renameSupplier.error ?? deleteSupplier.error) as Error)
                    ?.message
                }
              </p>
            )}
          </>
        )}
      </section>

      <section className="bg-card rounded-xl border p-6">
        <h2 className="mb-4 text-lg font-semibold">Lignes</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-muted-foreground text-left">
              <th className="pb-2 font-medium">Code</th>
              <th className="pb-2 font-medium">Fournisseur</th>
              <th className="pb-2 font-medium">Coupon / tranche&nbsp;($)</th>
              <th className="pb-2 font-medium">État</th>
              <th className="pb-2" />
            </tr>
          </thead>
          <tbody>
            {list.data?.lines.map((l) => (
              <tr key={l.id} className="border-t">
                <td className="py-3 font-mono font-medium">{l.code}</td>
                <td className="py-3">
                  <select
                    value={l.supplierId ?? ''}
                    onChange={(e) =>
                      assignSupplier.mutate({
                        id: l.id,
                        groupId: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    className="border-input bg-background h-9 rounded-lg border px-2 text-sm"
                  >
                    <option value="">— aucun —</option>
                    {supplierList.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="py-3">
                  <TrancheCell
                    key={l.couponTranche ?? 'none'}
                    line={l}
                    onSave={(v) => setTranche.mutate({ id: l.id, couponTranche: v })}
                  />
                </td>
                <td className="py-3">
                  {l.isActive ? (
                    <span className="text-green-700 dark:text-green-400">Active</span>
                  ) : (
                    <span className="text-muted-foreground">Désactivée</span>
                  )}
                </td>
                <td className="py-3 text-right">
                  <button
                    onClick={() => toggle.mutate({ id: l.id, isActive: !l.isActive })}
                    className="border-input rounded-lg border px-3 py-1.5 text-sm"
                  >
                    {l.isActive ? 'Désactiver' : 'Réactiver'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-muted-foreground mt-4 text-xs">
          Désactiver une ligne la retire du périmètre de tous les reps immédiatement. Les commandes
          déjà passées ne sont pas touchées. <b>Coupon / tranche</b> : montant d'achat qui donne 1
          coupon de tirage (laisser vide = la ligne n'en donne pas).
        </p>
      </section>
    </div>
  );
}

/** Cellule éditable de la tranche coupon d'une ligne. Enregistre à la perte de focus. */
function TrancheCell({ line, onSave }: { line: Line; onSave: (v: number | null) => void }) {
  const [v, setV] = useState(line.couponTranche ?? '');
  return (
    <input
      type="number"
      min={0}
      step="0.01"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        const num = v.trim() === '' || Number(v) <= 0 ? null : Number(v);
        const current = line.couponTranche == null ? null : Number(line.couponTranche);
        if (num !== current) onSave(num);
      }}
      placeholder="—"
      className="border-input bg-background h-9 w-28 rounded-lg border px-2 text-sm"
    />
  );
}

/** Ligne de la liste des fournisseurs : nom éditable, lignes rattachées, suppression. */
function SupplierRow({
  supplier,
  lignes,
  onRename,
  onDelete,
}: {
  supplier: Supplier;
  lignes: Line[];
  onRename: (nom: string) => void;
  onDelete: () => void;
}) {
  const [nom, setNom] = useState(supplier.name);
  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2">
      <input
        value={nom}
        onChange={(e) => setNom(e.target.value)}
        onBlur={() => nom.trim() && nom.trim() !== supplier.name && onRename(nom.trim())}
        className="border-input bg-background h-9 w-56 rounded-lg border px-2 font-medium"
      />
      <span className="text-muted-foreground text-sm">
        {lignes.length === 0
          ? 'aucune ligne'
          : lignes.map((l) => l.code).join(', ')}
      </span>
      <button
        onClick={() => {
          const msg =
            lignes.length > 0
              ? `Supprimer « ${supplier.name} » ? Ses ${lignes.length} ligne(s) seront détachées et ses cadeaux supprimés.`
              : `Supprimer le fournisseur « ${supplier.name} » ?`;
          if (confirm(msg)) onDelete();
        }}
        className="border-destructive/40 text-destructive ml-auto rounded-lg border px-3 py-1.5 text-sm font-medium"
      >
        Supprimer
      </button>
    </li>
  );
}
